import { isIos } from 'common/js/environment-utils';
import {
  isCeltraCreative,
  RedirectPartialConfig
} from 'common/js/format-helper';
import { isSpecialUrlScheme, isValidUrl } from 'common/js/url-utils';
import { MraidStore, MraidStoreProps } from 'common/stores/MraidStore';
import { UITimerStore } from 'common/stores/UITimerStore';
import { DisplayConfigType } from '../config';
import eventBus from 'common/event-handling/event-bus.model';
import { PreloadErrorEvent } from 'common/event-handling/events';
import { FormatState, FormatStateStore } from 'common/stores/FormatStateStore';

const MS_ADS_EVENTS_URL = 'ms-ads-events';

type MraidClickHandler = (url: string) => void;
type ClickHandler = (
  url: string,
  isRedirected: boolean,
  mraidOpen: MraidClickHandler
) => void;

export interface MraidStoreFactoryProps {
  uiTimerStore: UITimerStore;
  adContext: AdContext;
  formatConfig: DisplayConfigType;
  formatOptions: FormatOptions;
  formatName: FormatName;
  formatStateStore: FormatStateStore;
  redirectClickHandler: ClickHandler;
}

export function getMraidStore(props: MraidStoreFactoryProps) {
  const storeOptions: MraidStoreProps = {
    uiTimerStore: props.uiTimerStore,
    formatOptions: props.formatOptions,
    formatName: props.formatName,
    formatConfig: props.formatConfig
  };

  if (props.adContext.creativeSource === 'redirect') {
    storeOptions.onReady = () => {
      onMraidStoreReady(props.redirectClickHandler);
    };

    storeOptions.onViewable = () => {
      onMraidViewable(props.formatConfig);
    };

    storeOptions.onOpenCalled = (mraidOpen, url) => {
      onMraidOpenCalled(mraidOpen, url, props.redirectClickHandler);
    };

    storeOptions.onOpenNoRedirectCalled = (mraidOpen, url) => {
      onMraidOpenNoRedirectCalled(mraidOpen, url, props.redirectClickHandler);
    };

    storeOptions.onUnloadCalled = onUnloadCalled.bind(
      storeOptions,
      props.formatStateStore
    );
  }

  return new MraidStore(storeOptions);
}

function onMraidStoreReady(clickHandler: ClickHandler) {
  const onOpenedUrlListener: MRAIDOgyOnOpenedUrlListener = e => {
    window.mraid.removeEventListener('ogyOnOpenedUrl', onOpenedUrlListener);
    const isRedirected = e.url.indexOf(MS_ADS_EVENTS_URL) < 0;
    clickHandler(e.url, isRedirected, window.mraid.open as MraidClickHandler);
  };
  window.mraid.addEventListener('ogyOnOpenedUrl', onOpenedUrlListener);
}

function onMraidViewable(formatConfig: DisplayConfigType) {
  // Fix Celtra creative https://jira.ogury.io/browse/SL3-3010
  // The iOS SDK set a wrong width/size when the ad is loaded. So the Celtra creative can be lower than expected
  // The only solution is to dispatch a resize event to notify Celtra that its creative needs to resize.
  // Do it one do not work as the webview is not update everytime when a viewable state happen.

  if (isIos() && isCeltraCreative(formatConfig as RedirectPartialConfig)) {
    const resizeInterval = setInterval(() => {
      // @ts-ignore
      window.dispatchEvent(new CustomEvent('resize'), {
        detail: { isManual: true }
      });
    }, 50);
    setTimeout(() => clearInterval(resizeInterval), 1000);

    // Fix Celtra Bug after rotation of device for IOS
    window.addEventListener('resize', e => {
      // iOS need longer time to consider resize event
      // @ts-ignore
      if (!e || !e.detail || !e.detail.isManual) {
        const tmpInterval = setInterval(() => {
          window.dispatchEvent(
            new CustomEvent('resize', { detail: { isManual: true } })
          );
          window.dispatchEvent(
            new CustomEvent('orientationchange', {
              detail: { isManual: true }
            })
          );
        }, 100);
        setTimeout(() => clearInterval(tmpInterval), 500);
      }
    });
  }
}

function onMraidOpenCalled(
  mraidOpen: (url: string) => void,
  url: string,
  clickHandler: ClickHandler
) {
  if (!isValidUrl(url) && !isSpecialUrlScheme(url)) return;
  // For DCM Tag (INS tag), we use MRAID, and DCM already redirect their track to ms-ads-events
  // So we don't need to wrap it again
  // By default, we wrap it with click url (MS_ADS_EVENTS_URL)
  const isRedirected = url.indexOf(MS_ADS_EVENTS_URL) < 0;
  clickHandler(url, isRedirected, mraidOpen);
}

function onMraidOpenNoRedirectCalled(
  mraidOpen: (url: string) => void,
  url: string,
  clickHandler: ClickHandler
) {
  if (!isValidUrl(url) && !isSpecialUrlScheme(url)) return;
  clickHandler(url, false, mraidOpen);
}

function onUnloadCalled(
  formatStateStore: FormatStateStore,
  originalUnload: () => void
) {
  // When the formatState is set to AssetLoadingError
  // the load_error event has already been sent
  // so there's no need to dispatch the PreloadErrorEvent again.
  if (formatStateStore.state === FormatState.AssetLoadingError)
    return originalUnload();

  // Dispatch the PreloadErrorEvent to change the FormatState to AssetLoadingError.
  // Changing the FormatState to AssetLoadingError will automatically trigger the load_error event.
  window.mraid.unload = originalUnload;
  eventBus.publish(new PreloadErrorEvent());
}
